import { ApolloClient, type DocumentNode } from "@apollo/client";
import { MasterDataGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/MasterDataGraphQlAdapter";
import {
  CREATE_MASTER_DATA_DEDUCTION_TYPE,
  DELETE_ALL_MASTER_DATA_DEDUCTION_TYPES,
  DELETE_MASTER_DATA_DEDUCTION_TYPES,
  GET_DEDUCTION_TYPES_LOOKUP,
  GET_MASTER_DATA_DEDUCTION_SUB_TYPES,
  GET_MASTER_DATA_DEDUCTION_TYPES,
  UPDATE_MASTER_DATA_DEDUCTION_TYPE,
} from "@src/domain/graphql/masterData";
import {
  MasterDataDeductionType,
  type IMasterDataDeductionType,
  type IMasterDataDeductionTypeDeleteResult,
} from "@src/domain/entities/masterData";
import type {
  CreateMasterDataDeductionTypeDTO,
  DeductionTypesLookupItem,
  DeductionTypesLookupResponse,
  DeleteAllMasterDataDeductionTypesDTO,
  DeleteMasterDataDeductionTypesDTO,
  GetMasterDataDeductionSubTypesDTO,
  GetMasterDataDeductionTypesDTO,
  UpdateMasterDataDeductionTypeDTO,
} from "@src/domain/dto/masterData";
import type {
  IMasterDataDeductionTypeRepository,
  MasterDataDeductionTypesListResult,
} from "@src/domain/repositories/masterData";
import {
  requireMasterDataMutationData,
  throwIfMasterDataGraphQLError,
  throwIfMasterDataMutationError,
  toMasterDataListResult,
} from "./masterDataRepository.shared";

function mapTypeItem(item: IMasterDataDeductionType): MasterDataDeductionType {
  return MasterDataDeductionType.from({
    typeId: item.typeId,
    name: item.name,
    deductionTypeKey: item.deductionTypeKey,
    categoryId: item.categoryId ?? null,
    categoryName: item.categoryName ?? null,
    parentId: item.parentId ?? null,
    parentName: item.parentName ?? null,
    helpText: item.helpText ?? null,
    metadata: item.metadata ?? null,
    subTypeCount: item.subTypeCount ?? 0,
    isLinked: Boolean(item.isLinked),
    createdAt: item.createdAt ?? null,
    createdBy: item.createdBy ?? null,
    updatedAt: item.updatedAt ?? null,
    updatedBy: item.updatedBy ?? null,
  });
}

function toTypeMutationParams(
  params: CreateMasterDataDeductionTypeDTO | UpdateMasterDataDeductionTypeDTO,
) {
  return {
    name: params.name,
    deductionTypeKey: params.deductionTypeKey,
    deductionTypeCategoryId: params.deductionTypeCategoryId ?? null,
    parentId: params.parentId ?? null,
    helpText: params.helpText ?? null,
    metadata: params.metadata ?? null,
  };
}

function mapDeleteResult(
  payload: IMasterDataDeductionTypeDeleteResult,
): IMasterDataDeductionTypeDeleteResult {
  return {
    deletedCount: payload.deletedCount ?? 0,
    blockedCount: payload.blockedCount ?? 0,
    blockedIds: payload.blockedIds ?? [],
    message: payload.message ?? null,
  };
}

export class MasterDataDeductionTypeRepository
  implements IMasterDataDeductionTypeRepository
{
  private readonly client: ApolloClient;

  constructor(
    client: ApolloClient = MasterDataGraphQlAdapter.getInstance().getClient(),
  ) {
    this.client = client;
  }

  async getDeductionTypes(
    params: GetMasterDataDeductionTypesDTO,
  ): Promise<MasterDataDeductionTypesListResult> {
    const { data, error } = await this.client.query<{
      masterDataDeductionTypes: {
        items: IMasterDataDeductionType[];
        total: number;
        page: number;
        limit: number;
      };
    }>({
      query: GET_MASTER_DATA_DEDUCTION_TYPES,
      variables: {
        params: {
          search: params.search ?? null,
          child_search: params.child_search ?? null,
          page_number: params.page_number,
          page_size: params.page_size,
        },
      },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    throwIfMasterDataGraphQLError({ error });

    const response = data?.masterDataDeductionTypes;
    if (!response) {
      throw new Error("Failed to fetch master data deduction types");
    }

    return toMasterDataListResult(
      response,
      mapTypeItem,
      params.page_number,
      params.page_size,
    );
  }

  async getDeductionSubTypes(
    params: GetMasterDataDeductionSubTypesDTO,
  ): Promise<MasterDataDeductionTypesListResult> {
    const { data, error } = await this.client.query<{
      masterDataDeductionSubTypes: {
        items: IMasterDataDeductionType[];
        total: number;
        page: number;
        limit: number;
      };
    }>({
      query: GET_MASTER_DATA_DEDUCTION_SUB_TYPES,
      variables: {
        params: {
          parentId: params.parentId,
          search: params.search ?? null,
          page_number: params.page_number,
          page_size: params.page_size,
        },
      },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    throwIfMasterDataGraphQLError({ error });

    const response = data?.masterDataDeductionSubTypes;
    if (!response) {
      throw new Error("Failed to fetch master data deduction sub-types");
    }

    return toMasterDataListResult(
      response,
      mapTypeItem,
      params.page_number,
      params.page_size,
    );
  }

  async getDeductionTypesLookup(): Promise<{
    items: DeductionTypesLookupItem[];
    count: number;
  }> {
    const { data, error } = await this.client.query<DeductionTypesLookupResponse>({
      query: GET_DEDUCTION_TYPES_LOOKUP,
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    throwIfMasterDataGraphQLError({ error });

    const response = data?.deductionTypesLookup;
    if (!response) {
      throw new Error("Failed to fetch deduction types lookup");
    }

    return {
      items: (response.items ?? []).map((item) => ({
        id: item.id ?? null,
        parentId: item.parentId ?? null,
        deductionName: item.deductionName ?? null,
        deductionTypeKey: item.deductionTypeKey ?? null,
        subtypes: (item.subtypes ?? []).map((subtype) => ({
          id: subtype.id ?? null,
          parentId: subtype.parentId ?? null,
          deductionName: subtype.deductionName ?? null,
          deductionTypeKey: subtype.deductionTypeKey ?? null,
        })),
      })),
      count: response.count ?? 0,
    };
  }

  async createDeductionType(
    params: CreateMasterDataDeductionTypeDTO,
  ): Promise<MasterDataDeductionType> {
    return this.putDeductionType(
      CREATE_MASTER_DATA_DEDUCTION_TYPE,
      toTypeMutationParams(params),
      (data) => data?.createMasterDataDeductionType,
      "Failed to create deduction type",
    );
  }

  async updateDeductionType(
    params: UpdateMasterDataDeductionTypeDTO,
  ): Promise<MasterDataDeductionType> {
    return this.putDeductionType(
      UPDATE_MASTER_DATA_DEDUCTION_TYPE,
      {
        typeId: params.typeId,
        ...toTypeMutationParams(params),
      },
      (data) => data?.updateMasterDataDeductionType,
      "Failed to update deduction type",
    );
  }

  async deleteDeductionTypes(
    params: DeleteMasterDataDeductionTypesDTO,
  ): Promise<IMasterDataDeductionTypeDeleteResult> {
    const result = await this.client.mutate<{
      deleteMasterDataDeductionTypes?: IMasterDataDeductionTypeDeleteResult;
    }>({
      mutation: DELETE_MASTER_DATA_DEDUCTION_TYPES,
      variables: {
        params: {
          typeIds: params.typeIds,
        },
      },
      errorPolicy: "all",
    });

    throwIfMasterDataMutationError(result.error);

    return mapDeleteResult(
      requireMasterDataMutationData(
        result.data?.deleteMasterDataDeductionTypes,
        result.error,
        "Failed to delete deduction types",
      ),
    );
  }

  async deleteAllDeductionTypes(
    params: DeleteAllMasterDataDeductionTypesDTO,
  ): Promise<IMasterDataDeductionTypeDeleteResult> {
    const result = await this.client.mutate<{
      deleteAllMasterDataDeductionTypes?: IMasterDataDeductionTypeDeleteResult;
    }>({
      mutation: DELETE_ALL_MASTER_DATA_DEDUCTION_TYPES,
      variables: {
        params: {
          parentId: params.parentId ?? null,
        },
      },
      errorPolicy: "all",
    });

    throwIfMasterDataMutationError(result.error);

    return mapDeleteResult(
      requireMasterDataMutationData(
        result.data?.deleteAllMasterDataDeductionTypes,
        result.error,
        "Failed to delete deduction types",
      ),
    );
  }

  private async putDeductionType(
    mutation: DocumentNode,
    mutationParams: Record<string, unknown>,
    pickResponse: (
      data:
        | {
            createMasterDataDeductionType?: IMasterDataDeductionType;
            updateMasterDataDeductionType?: IMasterDataDeductionType;
          }
        | null
        | undefined,
    ) => IMasterDataDeductionType | null | undefined,
    fallbackMessage: string,
  ): Promise<MasterDataDeductionType> {
    const result = await this.client.mutate<{
      createMasterDataDeductionType?: IMasterDataDeductionType;
      updateMasterDataDeductionType?: IMasterDataDeductionType;
    }>({
      mutation,
      variables: { params: mutationParams },
      errorPolicy: "all",
    });

    throwIfMasterDataMutationError(result.error);

    return mapTypeItem(
      requireMasterDataMutationData(
        pickResponse(result.data),
        result.error,
        fallbackMessage,
      ),
    );
  }
}
